import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, MessageSquare, Rss, BarChart3, FileText, Calendar, Users, MoreVertical, Video,
  type LucideIcon,
} from 'lucide-react';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { workgroupAvatarSrc } from '@/lib/workgroup-avatar';
import { aurora, auroraPalette } from '@/components/aurora/tokens';
import { Sheet, SheetContent } from '@easyflow/ui-frontend/components/ui/sheet';
import { MobileTopBar, MobileHuddleStickyBanner } from '@/components/mobile/messenger';
import EventsTab from '@/pages/messenger/workgroup-hub/EventsTab';
import FilesTab from '@/pages/messenger/workgroup-hub/FilesTab';
import MembersTab from '@/pages/messenger/workgroup-hub/MembersTab';
import IdentityEditor from '@/pages/messenger/workgroup-hub/IdentityEditor';
import MobileWorkgroupChat from './MobileWorkgroupChat';
import MobileWorkgroupFeed from './MobileWorkgroupFeed';
import MobileWorkgroupPolls from './MobileWorkgroupPolls';
import type { Workgroup, WorkgroupHuddle } from '@/pages/messenger/workgroup-hub/types';
import type { WorkgroupHuddleStartResponse } from '@easyflow/shared';

type TabKey = 'chat' | 'feed' | 'polls' | 'files' | 'calendar' | 'membri';

const TABS: ReadonlyArray<{ key: TabKey; label: string; icon: LucideIcon }> = [
  { key: 'chat', label: 'Chat', icon: MessageSquare },
  { key: 'feed', label: 'Feed', icon: Rss },
  { key: 'polls', label: 'Polls', icon: BarChart3 },
  { key: 'files', label: 'Files', icon: FileText },
  { key: 'calendar', label: 'Calendar', icon: Calendar },
  { key: 'membri', label: 'Membri', icon: Users },
];

export default function MobileWorkgroupHub() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workgroup, setWorkgroup] = useState<Workgroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('chat');
  const [activeHuddle, setActiveHuddle] = useState<WorkgroupHuddle | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [startingHuddle, setStartingHuddle] = useState(false);
  const tabListRef = useRef<HTMLDivElement>(null);

  const tenantSlug = useMemo(() => window.location.pathname.split('/')[1] || '', []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<Workgroup>(`/messenger/workgroups/${id}`);
      setWorkgroup(res.data ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const probeHuddle = useCallback(async (): Promise<WorkgroupHuddle | null> => {
    if (!id) return null;
    try {
      const res = await apiClient.get<WorkgroupHuddle | null>(
        `/messenger/workgroups/${id}/huddle/active`,
      );
      return res.data ?? null;
    } catch {
      return null;
    }
  }, [id]);

  // Polling huddle attiva cross-tab — sticky banner sotto l'header.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const tick = async () => {
      const h = await probeHuddle();
      if (!cancelled) setActiveHuddle(h);
    };
    tick();
    const t = window.setInterval(tick, 15000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, [id, probeHuddle]);

  const currentUserId = user?.id ?? null;
  const isOwner = !!workgroup && !!currentUserId && workgroup.owner_id === currentUserId;
  const isAdmin = workgroup?.my_role === 'admin' || isOwner;

  function goBack() {
    navigate(`/${tenantSlug}/messenger/gruppi`);
  }

  function joinActiveHuddle() {
    if (activeHuddle?.meeting_link) {
      window.open(activeHuddle.meeting_link, '_blank', 'noopener,noreferrer');
    }
  }

  async function startHuddle(withRecording: boolean) {
    if (!workgroup) return;
    setStartingHuddle(true);
    try {
      const res = await apiClient.post<WorkgroupHuddleStartResponse>(
        `/messenger/workgroups/${workgroup.id}/huddle/start`,
        { record: withRecording },
      );
      setMenuOpen(false);
      const link = res.data?.room.meeting_link ?? null;
      if (link) window.open(link, '_blank', 'noopener,noreferrer');
      const refreshed = await probeHuddle();
      setActiveHuddle(refreshed);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore';
      alert(`Impossibile avviare Video Call Live: ${msg}`);
    } finally {
      setStartingHuddle(false);
    }
  }

  async function leaveWorkgroup() {
    if (!workgroup || !currentUserId) return;
    if (!confirm('Vuoi uscire da questo gruppo?')) return;
    setLeaving(true);
    try {
      await apiClient.delete(`/messenger/workgroups/${workgroup.id}/members/${currentUserId}`);
      setMenuOpen(false);
      goBack();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore';
      alert(`Impossibile uscire: ${msg}`);
    } finally {
      setLeaving(false);
    }
  }

  // Quando l'utente cambia tab, scrolla la pill selezionata in vista.
  useEffect(() => {
    const el = tabListRef.current?.querySelector<HTMLButtonElement>(`[data-tab="${tab}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [tab]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[100dvh] text-muted-foreground">
        <Loader2 className="w-7 h-7 animate-spin" />
      </div>
    );
  }

  if (error || !workgroup) {
    return (
      <div className="flex flex-col h-[100dvh] bg-background">
        <MobileTopBar title="Gruppo" onBack={goBack} />
        <div className="p-6 text-center">
          <p className="text-red-400 mb-3">{error ?? 'Gruppo non trovato'}</p>
          <button onClick={goBack} className="text-blue-400 text-sm">← Torna ai gruppi</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[100dvh] bg-background">
      <MobileTopBar
        title={workgroup.name}
        subtitle={workgroup.description ?? undefined}
        onBack={goBack}
        backLabel="Torna ai gruppi"
        leading={(
          <div
            className="w-9 h-9 rounded-lg bg-muted border border-border mr-1 flex items-center justify-center text-foreground text-xs font-semibold overflow-hidden"
            aria-hidden
          >
            {workgroup.avatar_url ? (
              <img src={workgroupAvatarSrc(workgroup)} alt="" className="w-full h-full object-cover" />
            ) : (
              workgroup.name.substring(0, 2).toUpperCase()
            )}
          </div>
        )}
        actions={[{
          id: 'menu',
          label: 'Menu gruppo',
          icon: <MoreVertical className="w-5 h-5" />,
          onClick: () => setMenuOpen(true),
        }]}
      />

      <MobileHuddleStickyBanner
        active={!!activeHuddle}
        label={`Video Call Live in corso · ${workgroup.name}`}
        subtitle={activeHuddle?.started_by_name ? `Avviato da ${activeHuddle.started_by_name}` : undefined}
        recording={!!activeHuddle?.recording_requested}
        onJoin={joinActiveHuddle}
      />

      <div
        ref={tabListRef}
        role="tablist"
        aria-label="Sezioni gruppo"
        className="flex gap-1.5 overflow-x-auto px-3 py-2 border-b border-border bg-background/95 backdrop-blur-md"
      >
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              data-tab={key}
              id={`mwg-tab-${key}`}
              role="tab"
              aria-selected={active}
              aria-controls={`mwg-panel-${key}`}
              tabIndex={active ? 0 : -1}
              onClick={() => setTab(key)}
              className={cn(
                'shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition min-h-[36px]',
                active
                  ? 'text-white border-transparent'
                  : 'bg-muted text-muted-foreground border-border',
              )}
              style={active ? { background: auroraPalette.indigo } : undefined}
              data-testid={`mobile-wg-tab-${key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto">
        {TABS.map(({ key }) => (
          <div
            key={key}
            id={`mwg-panel-${key}`}
            role="tabpanel"
            aria-labelledby={`mwg-tab-${key}`}
            hidden={tab !== key}
            className="h-full"
          >
            {tab === key && key === 'chat' && (
              <MobileWorkgroupChat workgroup={workgroup} currentUserId={currentUserId} />
            )}
            {tab === key && key === 'feed' && (
              <MobileWorkgroupFeed workgroup={workgroup} currentUserId={currentUserId} canManage={!!isAdmin} />
            )}
            {tab === key && key === 'polls' && (
              <MobileWorkgroupPolls workgroup={workgroup} />
            )}
            {tab === key && key === 'files' && (
              <FilesTab workgroup={workgroup} canManage={!!isAdmin} />
            )}
            {tab === key && key === 'calendar' && (
              <EventsTab workgroup={workgroup} canManage={!!isAdmin} />
            )}
            {tab === key && key === 'membri' && (
              <MembersTab workgroup={workgroup} canManage={!!isAdmin} currentUserId={currentUserId} />
            )}
          </div>
        ))}
      </div>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto p-4 flex flex-col gap-4">
          <h2 className={cn(aurora.type.title, 'text-foreground')}>Impostazioni gruppo</h2>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => startHuddle(false)}
              disabled={startingHuddle || !!activeHuddle}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white text-sm font-medium disabled:opacity-50"
              style={{ background: auroraPalette.indigo }}
              data-testid="mobile-wg-start-huddle"
            >
              <Video className="w-4 h-4" />
              {activeHuddle ? 'Video Call Live già in corso' : (startingHuddle ? 'Avvio…' : 'Avvia Video Call Live')}
            </button>
            <button
              type="button"
              onClick={() => startHuddle(true)}
              disabled={startingHuddle || !!activeHuddle}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-red-500/40 bg-red-500/10 text-red-300 text-sm font-medium disabled:opacity-50"
              data-testid="mobile-wg-start-huddle-rec"
            >
              <Video className="w-4 h-4" />
              {startingHuddle ? 'Avvio…' : 'Avvia Video Call Live con registrazione'}
            </button>
          </div>

          <IdentityEditor
            workgroup={workgroup}
            canEdit={!!isAdmin}
            isOwner={isOwner}
            onUpdated={(wg) => setWorkgroup(wg)}
          />

          {!isOwner && (
            <button
              type="button"
              onClick={leaveWorkgroup}
              disabled={leaving}
              className="w-full px-4 py-3 rounded-xl border border-red-500/40 bg-red-500/10 text-red-300 text-sm font-medium disabled:opacity-50"
              data-testid="mobile-wg-leave"
            >
              {leaving ? 'Uscita in corso…' : 'Esci dal gruppo'}
            </button>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
